// Layer 4 (UI) — Quality & Lab's small glass pieces, per the locked comp
// `.impeccable/mocks/decision/quality.html` (A): the state chip recipe
// (DESIGN.md § Type ramp → chips), the mono case-code chip, one strip cell in
// the Advisory strip recipe, the diverging mass-balance bar, and a titled
// metric row. Presentational only; every value arrives from a transform.

import type { ReactNode } from 'react';
import { cn } from '@/shared/cn';
import type { DivergingBarGeometry, QualityChipTone } from './Scene.transform';

const CHIP_BASE =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';

// Sample-adherence states are NOT drawn here: they render through the shared
// `StateChip` / `SAMPLE_ADHERENCE_CHIP` so `/quality` and `/compliance` tone
// DUE the same (issue #658 round 2). This map covers the surface's own status
// chips only — mass-balance CLOSED / REVIEW, penalty OK / WATCH / OVER LIMIT.
const CHIP_TONE: Record<QualityChipTone, string> = {
  ok: 'bg-[color-mix(in_srgb,var(--state-online)_12%,transparent)] text-[var(--state-online)]',
  warn: 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]',
  bad: 'bg-[color-mix(in_srgb,var(--state-blocked)_12%,transparent)] text-[var(--state-blocked)] dark:bg-[color-mix(in_srgb,var(--state-blocked)_22%,transparent)]',
  neutral: 'bg-[var(--hairline)] text-ink-secondary',
};

/** A status chip in the comp's tone recipe (DESIGN.md § Type ramp → chips):
 *  state colour marks state and nothing else. Adherence states use the shared
 *  `StateChip`; this is for the surface's non-adherence statuses. */
export function StateChipPill({ tone, children }: { tone: QualityChipTone; children: ReactNode }) {
  return <span data-state-chip={tone} className={cn(CHIP_BASE, CHIP_TONE[tone])}>{children}</span>;
}

/** The case's seeded code as a mono chip that opens the case drawer. */
export function CaseCodeChip({ code, onOpen, label }: { code: string; onOpen: () => void; label: string }) {
  return (
    <button
      type="button"
      data-case-chip={code}
      aria-label={`Open case ${code} · ${label}`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      className="inline-flex items-center whitespace-nowrap rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-2 py-px font-mono text-xs leading-4 text-ink-primary transition-colors duration-150 hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
    >
      {code}
    </button>
  );
}

/** Rotated 10px notch on a strip cell's inner boundary (DESIGN.md § Strip and stepper). */
const NOTCH =
  "after:pointer-events-none after:absolute after:right-[-6px] after:top-1/2 after:z-10 after:h-[10px] after:w-[10px] after:-translate-y-1/2 after:rotate-45 after:border-r after:border-t after:border-[var(--hairline-strong)] after:content-['']";

const SELECTED_FACE =
  'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] shadow-[inset_0_-3px_0_var(--color-accent-500)] after:bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))]';

/** One strip cell: caps stage name, primary figure with its context on the
 *  baseline, comparison lines, a foot for chips. Selectable cells are
 *  role=button so a case chip can sit inside them. */
export function StripCell({
  id,
  name,
  figure,
  figureContext,
  children,
  foot,
  selected = false,
  onSelect,
  notch = true,
  quiet = false,
  className,
}: {
  id: string;
  name: string;
  figure: ReactNode;
  figureContext?: string;
  children?: ReactNode;
  foot?: ReactNode;
  selected?: boolean;
  onSelect?: () => void;
  notch?: boolean;
  /** A process-condition node on the arsenic strip: lighter face, smaller value. */
  quiet?: boolean;
  className?: string;
}) {
  const face = selected
    ? SELECTED_FACE
    : quiet
      ? 'bg-[var(--surface-1)] after:bg-[var(--surface-1)]'
      : cn('bg-[var(--surface-2)] after:bg-[var(--surface-2)]', onSelect ? 'hover:bg-[var(--surface-1)] hover:after:bg-[var(--surface-1)]' : '');
  return (
    <div
      data-strip-cell={id}
      data-selected={selected ? 'true' : undefined}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-pressed={onSelect ? selected : undefined}
      onClick={onSelect}
      onKeyDown={
        onSelect
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
      className={cn(
        'relative flex min-w-0 flex-col gap-1 border-r border-[var(--hairline)] py-3 transition-colors duration-150 last:border-r-0 last:after:hidden',
        quiet ? 'pl-3 pr-2.5' : 'pl-4 pr-3.5',
        onSelect ? 'cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]' : '',
        notch ? NOTCH : '',
        face,
        className,
      )}
    >
      <span className="break-words text-xs font-semibold uppercase tracking-[0.07em] text-muted">{name}</span>
      {quiet ? (
        <span className="break-words text-sm font-semibold leading-snug text-ink-secondary" data-strip-figure={id}>{figure}</span>
      ) : (
        <span
          className="flex flex-wrap items-baseline gap-x-1 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary"
          data-strip-figure={id}
        >
          {figure}
          {figureContext ? (
            <span className="text-xs font-semibold tracking-normal text-muted">{figureContext}</span>
          ) : null}
        </span>
      )}
      {children}
      {foot ? <span className="mt-auto flex min-w-0 flex-wrap items-center gap-1.5 pt-1">{foot}</span> : null}
    </div>
  );
}

/** A comparison line: an optional muted prefix, the lead term in 600 ink-secondary, the rest muted. */
export function Comparison({ pre, lead, rest, className }: { pre?: string; lead?: string; rest?: string; className?: string }) {
  return (
    <span className={cn('break-words text-xs leading-snug tabular-nums text-muted', className)}>
      {pre}
      {lead ? <span className="font-semibold text-ink-secondary">{lead}</span> : null}
      {rest}
    </span>
  );
}

/** A panel's primary figure with its comparison beside it (DESIGN.md § The primary figure). */
export function PanelFigure({
  figure,
  figureContext,
  lead,
  rest,
  id,
}: {
  figure: string;
  figureContext?: string;
  lead: string;
  rest: string;
  id: string;
}) {
  return (
    <p className="mb-3 flex flex-wrap items-baseline gap-x-2.5 gap-y-1" data-panel-figure={id}>
      <span className="text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
        {figure}
        {figureContext ? <span className="text-xs font-semibold tracking-normal text-muted">{` ${figureContext}`}</span> : null}
      </span>
      <Comparison lead={lead} rest={rest} />
    </p>
  );
}

/** Mass-balance error on a diverging track: zero at the centre, the ±
 *  tolerance shaded, the fill running left or right of zero. */
export function DivergingBar({ geometry, tone, scalePct, tolerancePct }: {
  geometry: DivergingBarGeometry;
  tone: 'ok' | 'warn';
  scalePct: number;
  tolerancePct: number;
}) {
  const fill = tone === 'ok' ? 'var(--state-online)' : 'var(--state-warning)';
  const ticks = [-scalePct, -tolerancePct, 0, tolerancePct, scalePct];
  return (
    <div>
      <div className="relative h-2.5 rounded-[3px] bg-[var(--hairline)]">
        <span
          className="absolute inset-y-0 border-x border-dashed border-[var(--hairline-strong)] bg-[color-mix(in_srgb,var(--ink-secondary)_16%,transparent)]"
          style={{ left: `${geometry.bandLeftPct}%`, width: `${geometry.bandWidthPct}%` }}
        />
        <span className="absolute inset-y-[2px] rounded-[2px]" style={{ left: `${geometry.leftPct}%`, width: `${geometry.widthPct}%`, backgroundColor: fill }} />
        <span className="absolute -inset-y-[3px] left-1/2 w-[1.5px] bg-[var(--ink-primary)]" />
      </div>
      <div className="mt-1 flex justify-between text-[10px] tabular-nums text-muted">
        {ticks.map((tick) => (
          <span key={tick}>{tick === 0 ? '0' : `${tick > 0 ? '+' : '−'}${Math.abs(tick)}%`}</span>
        ))}
      </div>
    </div>
  );
}
